<template>
  <div class="dashboard-container">
    <div class="dashboard-layout" :class="{ 'no-sidebar': authStore.userType !== 'Staff' }">
      <!-- Upcoming Events Sidebar - Only visible for Staff -->
      <div v-show="authStore.userType === 'Staff'" class="events-sidebar">
        <EventsComponents :eventlimit="3" ref="eventsComponentRef"  />
      </div>

      <!-- Dashboard Cards - Adjust layout based on user type -->
      <!-- Dashboard Cards in a single container -->
      <div
        class="card-container backdrop-blur-sm bg-white/80 border border-white/20 rounded-lg p-4 shadow-sm"
      >
        <!-- Student Info Section -->
        <div v-if="authStore.userType === 'Parent'" class="group-header">
          Child Info

          <div class="line-left ml-2"></div>
          <div class="line-right"></div>
        </div>
        <div v-else class="group-header">
          Student Info

          <div class="line-left ml-2"></div>
          <div class="line-right"></div>
        </div>
        <div class="card-grid">
          <!-- Child Information Card -->
          <div
            @click="showDialog2 = true"
            class="dashboard-card backdrop-blur-sm bg-white/80 border border-white/20 rounded-lg p-4 hover:translate-y-[-8px] transition-all duration-300 cursor-pointer shadow-sm"
          >
            <div class="flex justify-between items-start mb-2">
              <div class="bg-gradient-to-br from-blue-400 to-sky-600 p-2 rounded-lg shadow-sm">
                <i class="pi pi-user text-white text-base"></i>
              </div>
              <Badge :value="studentCount" severity="info"></Badge>
            </div>
            <h3 v-if="authStore.userType === 'Parent'" class="text-base font-semibold text-gray-700 mb-1">Child Information</h3>
            <h3 v-else class="text-base font-semibold text-gray-700 mb-1">Student Information</h3>
            <div v-if="authStore.userType === 'Parent'" class="text-xl font-bold text-gray-800 mb-2">{{ studentCount }} Children</div>
            <div v-else class="text-xl font-bold text-gray-800 mb-2">{{ studentCount }} Student</div>
            <div class="flex justify-between items-center">
              <div class="flex gap-0.5">
                <Tag value="Profiles" severity="info" class="text-xs"></Tag>
                <Tag value="Records" severity="info" class="text-xs"></Tag>
              </div>
              <Button
                icon="pi pi-arrow-right"
                class="p-button-rounded p-button-raised p-button-sm"
                style="width: 24px; height: 24px"
              ></Button>
            </div>
          </div>

          <!-- Child Attendance Card -->
          <div
            class="dashboard-card backdrop-blur-sm bg-white/80 border border-white/20 rounded-lg p-4 hover:translate-y-[-8px] transition-all duration-300 cursor-pointer shadow-sm"
          >
            <div class="flex justify-between items-start mb-2">
              <div class="bg-gradient-to-br from-orange-400 to-amber-600 p-2 rounded-lg shadow-sm">
                <i class="pi pi-clock text-white text-base"></i>
              </div>
              <Badge value="18" severity="warning"></Badge>
            </div>
            <h3 v-if="authStore.userType === 'Parent'" class="text-base font-semibold text-gray-700 mb-1">Child Attendance</h3>
            <h3 v-else class="text-base font-semibold text-gray-700 mb-1">Student Attendance</h3>
            <div class="text-xl font-bold text-gray-800 mb-2">18</div>
            <div class="flex justify-between items-center">
              <span
                class="px-1.5 py-0.5 bg-amber-50 text-amber-700 rounded-full text-xs font-medium"
                >Requires action</span
              >
              <Button
                icon="pi pi-eye"
                class="p-button-sm p-button-warning"
                style="height: 24px"
              ></Button>
            </div>
          </div>

          <!-- Child performance -->
          <div
            @click="router.push('/childperformance')"
            class="dashboard-card backdrop-blur-sm bg-white/80 border border-white/20 rounded-lg p-4 hover:translate-y-[-8px] transition-all duration-300 cursor-pointer shadow-sm"
          >
            <div class="flex justify-between items-start mb-2">
              <div class="bg-gradient-to-br from-purple-400 to-violet-600 p-2 rounded-lg shadow-sm">
                <i class="pi pi-chart-bar text-white text-base"></i>
              </div>
              <div
                class="flex items-center text-xs font-medium text-purple-600 bg-purple-100 px-1.5 rounded-full"
              >
                <i class="pi pi-arrow-up mr-0.5"></i>
                <span>+12%</span>
              </div>
            </div>
            <h3 v-if="authStore.userType === 'Parent'" class="text-base font-semibold text-gray-700 mb-1">Child Performance</h3>
            <h3 v-else class="text-base font-semibold text-gray-700 mb-1">Student Performance</h3>
            <div class="text-xl font-bold text-gray-800 mb-2">Performance</div>
            <div class="flex justify-between items-center">
              <div class="flex items-center gap-0.5">
                <i class="pi pi-file-pdf text-purple-600 text-xs"></i>
                <span
                  class="px-1.5 py-0.5 bg-blue-50 text-blue-700 rounded-full text-xs font-medium"
                  >Grades</span
                >
              </div>
              <Button
                icon="pi pi-arrow-right"
                class="p-button-rounded p-button-raised p-button-sm"
                style="width: 24px; height: 24px"
              ></Button>
            </div>
          </div>

          <!-- Tuition Payments Card -->
          <div
            class="dashboard-card backdrop-blur-sm bg-white/80 border border-white/20 rounded-lg p-4 hover:translate-y-[-8px] transition-all duration-300 cursor-pointer shadow-sm"
          >
            <div class="flex justify-between items-start mb-2">
              <div class="bg-gradient-to-br from-rose-400 to-red-600 p-2 rounded-lg shadow-sm">
                <i class="pi pi-wallet text-white text-base"></i>
              </div>
              <div
                class="flex items-center text-xs font-medium text-red-600 bg-red-100 px-1.5 rounded-full"
              >
                <i class="pi pi-exclamation-circle mr-0.5"></i>
                <span>Due Soon</span>
              </div>
            </div>
            <h3 class="text-base font-semibold text-gray-700 mb-1">Tuition Payments</h3>
            <div class="text-xl font-bold text-gray-800 mb-2">$750.00</div>
            <div class="flex justify-between items-center">
              <span class="px-1.5 py-0.5 bg-red-50 text-red-700 rounded-full text-xs font-medium"
                >Due in 5 days</span
              >
              <Button
                icon="pi pi-credit-card"
                class="p-button-sm p-button-danger"
                style="height: 24px"
              ></Button>
            </div>
          </div>
        </div>

        <!-- Others Section -->
        <div class="group-header mt-6">
          Events/Interaction

          <div class="line-left ml-2"></div>

          <div class="line-right"></div>
        </div>
        <div class="card-grid">
          <!-- Calendar Card -->
          <div
            @click="router.push('/calendar')"
            class="dashboard-card backdrop-blur-sm bg-white/80 border border-white/20 rounded-lg p-4 hover:translate-y-[-8px] transition-all duration-300 cursor-pointer shadow-sm"
          >
            <div class="flex justify-between items-start mb-2">
              <div class="bg-gradient-to-br from-teal-400 to-emerald-600 p-2 rounded-lg shadow-sm">
                <i class="pi pi-calendar text-white text-base"></i>
              </div>
              <Badge value="3" severity="warning"></Badge>
            </div>
            <h3 class="text-base font-semibold text-gray-700 mb-1">Calendar</h3>
            <div class="text-xl font-bold text-gray-800 mb-2">{{ formattedDate }}</div>

            <div class="flex justify-between items-center">
              <div class="flex gap-1 flex-wrap">
                <Tag value="Classes" severity="success" class="text-xs"></Tag>
                <Tag value="Meetings" severity="warning" class="text-xs"></Tag>
              </div>
              <Button
                icon="pi pi-arrow-right"
                class="p-button-rounded p-button-raised p-button-sm"
              ></Button>
            </div>
          </div>

          <!-- Messages Card -->
          <div
            @click="router.push('/chat')"
            class="dashboard-card backdrop-blur-sm bg-white/80 border border-white/20 rounded-lg p-4 hover:translate-y-[-8px] transition-all duration-300 cursor-pointer shadow-sm"
          >
            <div class="flex justify-between items-start mb-2">
              <div class="bg-gradient-to-br from-blue-400 to-sky-600 p-2 rounded-lg shadow-sm">
                <i class="pi pi-envelope text-white text-base"></i>
              </div>
              <Badge value="27" severity="info"></Badge>
            </div>
            <h3 class="text-base font-semibold text-gray-700 mb-1">Messages</h3>
            <div class="text-xl font-bold text-gray-800 mb-2">27</div>
            <div class="flex justify-between items-center">
              <span class="px-2 py-1 bg-blue-50 text-blue-700 rounded-full text-xs font-medium"
                >11 unread</span
              >
              <Button label="Inbox" icon="pi pi-inbox" class="p-button-sm p-button-info"></Button>
            </div>
          </div>

          <!-- School Events Card -->
          <div
           @click="openEventsDialog"
            class="dashboard-card backdrop-blur-sm bg-white/80 border border-white/20 rounded-lg p-4 hover:translate-y-[-8px] transition-all duration-300 cursor-pointer shadow-sm"
          >
            <div class="flex justify-between items-start mb-2">
              <div class="bg-gradient-to-br from-amber-400 to-yellow-600 p-2 rounded-lg shadow-sm">
                <i class="pi pi-flag text-white text-base"></i>
              </div>
              <Badge value="New" severity="success"></Badge>
            </div>
            <h3 class="text-base font-semibold text-gray-700 mb-1">School Events</h3>
            <div class="text-xl font-bold text-gray-800 mb-2">{{ eventsCount }} Events</div>
            <div class="flex justify-between items-center">
              <div class="flex items-center gap-1">
                <i class="pi pi-calendar text-yellow-600 text-xs"></i>
                <span class="text-xs font-medium">Sports Day</span>
              </div>
              <Button
                icon="pi pi-arrow-right"
                class="p-button-rounded p-button-raised p-button-sm"
              ></Button>
            </div>
          </div>
        </div>

        <div class="group-header mt-6">
          Profile and Configuration

          <div class="line-left ml-2"></div>

          <div class="line-right"></div>
        </div>

        <div class="card-grid">
          <!-- Profile Card -->
          <div @click="ViewProfile"
            class="dashboard-card backdrop-blur-sm bg-white/80 border border-white/20 rounded-lg p-4 hover:translate-y-[-8px] transition-all duration-300 cursor-pointer shadow-sm"
          >
            <div class="flex justify-between items-start mb-2">
              <div class="bg-gradient-to-br from-green-400 to-emerald-600 p-2 rounded-lg shadow-sm">
                <i class="pi pi-id-card text-white text-base"></i>
              </div>
              <div
                class="flex items-center text-xs font-medium text-green-600 bg-green-100 px-1.5 rounded-full"
              >
                <i class="pi pi-arrow-up mr-0.5"></i>
                <!-- <span>+8%</span> -->
              </div>
            </div>
            <h3 class="text-base font-semibold text-gray-700 mb-1">Profile</h3>
            <div class="text-xl font-bold text-gray-800 mb-2">Personal info</div>
            <div class="flex justify-between items-center">
              <div class="flex items-center gap-1">
                <i class="pi pi-sync text-green-600 text-xs"></i>
                <span class="text-xs font-medium">Updated today</span>
              </div>
              <Button

                icon="pi pi-arrow-right"
                class="p-button-rounded p-button-raised p-button-sm"
              ></Button>
            </div>
          </div>

          <!-- Settings Card -->
          <div
            class="dashboard-card backdrop-blur-sm bg-white/80 border border-white/20 rounded-lg p-4 hover:translate-y-[-8px] transition-all duration-300 cursor-pointer shadow-sm"
          >
            <div class="flex justify-between items-start mb-2">
              <div class="bg-gradient-to-br from-indigo-400 to-indigo-600 p-2 rounded-lg shadow-sm">
                <i class="pi pi-cog text-white text-base"></i>
              </div>
              <div class="text-xs font-medium text-indigo-600 px-1.5 bg-indigo-100 rounded-full">
                Admin
              </div>
            </div>
            <h3 class="text-base font-semibold text-gray-700 mb-1">Settings</h3>
            <div class="text-xl font-bold text-gray-800 mb-2">Config</div>
            <div class="flex justify-between items-center">
              <div class="flex items-center gap-1">
                <i class="pi pi-sliders-h text-indigo-600 text-xs"></i>
                <span class="text-xs font-medium">Preferences</span>
              </div>
              <Button
                icon="pi pi-arrow-right"
                class="p-button-rounded p-button-raised p-button-sm"
              ></Button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Chat Bubble Component - Completely separated from the main layout -->
    <div class="chat-bubble-container">
      <!-- Chat Bubble Button -->
      <button @click="toggleChat" class="chat-bubble-button" :class="{ active: isChatOpen }">
        <i class="pi" :class="isChatOpen ? 'pi-times' : 'pi-comments'"></i>
        <Badge
          v-if="!isChatOpen && unreadCount > 0"
          :value="unreadCount"
          severity="danger"
          class="chat-bubble-badge"
        ></Badge>
      </button>

      <!-- Chat Box -->
      <Transition name="chat-slide">
        <div v-if="isChatOpen" class="chat-box">
          <!-- Chat Header with rounded top corners -->
          <div class="chat-header rounded-t-lg">
            <div class="flex items-center">
              <div
                class="w-8 h-8 rounded-full bg-gradient-to-r from-indigo-500 to-purple-600 flex items-center justify-center text-white font-medium mr-2"
              >
                <i class="pi pi-user"></i>
              </div>
              <div>
                <div class="font-semibold text-gray-800">Support</div>
                <div class="text-xs text-green-500 flex items-center">
                  <span class="w-2 h-2 rounded-full bg-green-500 mr-1"></span>
                  Online
                </div>
              </div>
            </div>
            <div class="flex items-center gap-2">
              <button class="chat-header-button">
                <i class="pi pi-phone text-sm"></i>
              </button>
              <button class="chat-header-button">
                <i class="pi pi-video text-sm"></i>
              </button>
              <button class="chat-header-button" @click="toggleChat">
                <i class="pi pi-minus text-sm"></i>
              </button>
            </div>
          </div>

          <!-- Chat Messages -->
          <div class="chat-messages" ref="messagesContainer">
            <div
              v-for="(message, index) in messages"
              :key="index"
              class="message"
              :class="message.sender === 'support' ? 'message-support' : 'message-user'"
            >
              <div class="message-text">
                {{ message.text }}
              </div>
              <div class="message-time">{{ message.time }}</div>
            </div>
          </div>

          <!-- Chat Input -->
          <div class="chat-input rounded-b-lg">
            <button class="chat-input-button">
              <i class="pi pi-paperclip"></i>
            </button>
            <input
              type="text"
              v-model="newMessage"
              placeholder="Type a message..."
              class="chat-input-field"
              @keyup.enter="sendMessage"
            />
            <button class="chat-input-button" @click="sendMessage">
              <i class="pi pi-send"></i>
            </button>
          </div>
        </div>
      </Transition>

      <!-- New Message Notification -->
      <Transition name="notification-pop">
        <div
          v-if="!isChatOpen && showNotification"
          class="chat-notification rounded-lg"
          @click="toggleChat"
        >
          <div class="flex items-start">
            <div
              class="w-10 h-10 rounded-full bg-gradient-to-r from-indigo-500 to-purple-600 flex items-center justify-center text-white font-medium mr-2"
            >
              <i class="pi pi-user"></i>
            </div>
            <div>
              <div class="font-semibold text-gray-800">Support</div>
              <div class="text-sm">{{ latestMessage.text }}</div>
            </div>
          </div>
          <button class="notification-close" @click.stop="dismissNotification">
            <i class="pi pi-times text-xs"></i>
          </button>
        </div>
      </Transition>
    </div>

        <Dialog
            v-model:visible="showDialog2"
            modal
            :style="{ width: '70vw', position: 'absolute', top: '5vh' }"
            position="top"
        >
         <StudentViewComponents :schoolIdx="parent_id" :callFrom="'ParentDB'" />
        </Dialog>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, nextTick, watchEffect, onBeforeMount } from 'vue'
import Button from 'primevue/button'
import Badge from 'primevue/badge'
import Tag from 'primevue/tag'
import { useRouter } from 'vue-router'
import { useAuthStore } from '@/auth/auth'

import { useEventStore } from '@/store/admin/EventStore'
import { storeToRefs } from 'pinia'
import { useDbCountStore } from '@/store/admin/DashboardCountStore'

import EventsComponents from '@/components/EventsComponents.vue'
import StudentViewComponents from '@/components/students/StudentViewComponents.vue'
import { usedStudentStore } from '@/store/admin/StudentStore'



const router = useRouter()
const authStore = useAuthStore()
const dbCountStore = useDbCountStore()
const studentStore = usedStudentStore()
const { students,studentCount } = storeToRefs(studentStore)

const { eventsCount } = storeToRefs(dbCountStore)

const eventStore = useEventStore()
const { eventx } = storeToRefs(eventStore)
eventStore.getEventPID(authStore.school_id)

const showDialog2 = ref(false)

function ViewProfile() {
  if( authStore.userType === 'Staff'){
      router.push(`/staff/profile/${authStore.staff_pid.replace(/"/g, '')}`);
  }else{
    router.push('/parent/profile/' + authStore.parent_pid.replace(/"/g, ''));
  }
}

const eventsComponentRef = ref(null)

// Function to trigger the events dialog from main component
const openEventsDialog = () => {
  if (eventsComponentRef.value) {
    eventsComponentRef.value.openAllEventsDialog()
  }
}

// You can also access other exposed properties/methods
const getEventsData = () => {
  if (eventsComponentRef.value) {
    console.log('Current events:', eventsComponentRef.value.events)
    console.log('All events:', eventsComponentRef.value.allEvents)
  }
}

// Transform raw event data into display format
const events = computed(() => {
  if (!eventx.value || !eventx.value.data) return []

  // Transform all events first
  const transformedEvents = eventx.value.data.map((event) => {
    const fromDate = formatEventDate(event.event_from_date)
    return {
      pid: event.pid,
      month: fromDate.month,
      date: fromDate.day,
      title: event.name,
      description: event.description,
      time: formatEventTime(event.event_from_time, event.event_to_time),
      location: event.school?.name || 'System-wide Event',
      type: event.event_type.description,
      severity: getSeverity(event.event_type.type),
    }
  })

  // Return only the first 5 events
  return transformedEvents.slice(0, 5)
})

// Helper functions for formatting
function formatEventDate(dateStr) {
  const date = new Date(dateStr)
  return {
    month: date.toLocaleString('en-US', { month: 'short' }),
    day: date.getDate(),
  }
}

function formatEventTime(startTime, endTime) {
  // Parse the time strings (format is "HH:MM:SS")
  const formatTime = (timeStr) => {
    const [hours, minutes] = timeStr.split(':')
    const hour = parseInt(hours, 10)
    const minute = parseInt(minutes, 10)
    const period = hour >= 12 ? 'PM' : 'AM'
    const hour12 = hour % 12 || 12
    return `${hour12}:${minutes.padStart(2, '0')} ${period}`
  }

  return `${formatTime(startTime)} - ${formatTime(endTime)}`
}

// Map severity based on event_type
function getSeverity(eventType) {
  const typeMap = {
    regular_holiday: 'success',
    special_working_holiday: 'warning',
    special_non_working_holiday: 'danger',
    // Add other types as needed
  }
  return typeMap[eventType] || 'info'
}

// Debug log for events data
watchEffect(() => {
  if (eventx.value && eventx.value.data) {
    // console.log('Events data:', events.value)
  }
})

// Chat component state
const isChatOpen = ref(false)
const newMessage = ref('')
const showNotification = ref(false)
const messagesContainer = ref(null)

// Sample messages
const messages = ref([
  {
    sender: 'support',
    text: 'Hello! How can I help you today?',
    time: '10:30 AM',
  },
  {
    sender: 'user',
    text: 'I need information about the upcoming parent-teacher conference.',
    time: '10:32 AM',
  },
  {
    sender: 'support',
    text: 'Of course! The parent-teacher conference is scheduled for next Friday from 2:00 PM to 5:00 PM. Would you like me to send you the detailed schedule?',
    time: '10:33 AM',
  },
])

// Computed properties
const unreadCount = ref(2) // Set to 2 to match the screenshot
const latestMessage = computed(() => {
  const supportMessages = messages.value.filter((msg) => msg.sender === 'support')
  return supportMessages[supportMessages.length - 1] || { text: '' }
})

// Functions
const toggleChat = () => {
  isChatOpen.value = !isChatOpen.value
  if (isChatOpen.value) {
    unreadCount.value = 0
    showNotification.value = false
    nextTick(() => {
      scrollToBottom()
    })
  }
}
const currentTime = ref(new Date())

const formattedDate = computed(() => {
  return currentTime.value.toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  })
})
const sendMessage = () => {
  if (newMessage.value.trim() === '') return

  // Add user message
  const now = new Date()
  const timeString = now.getHours() + ':' + (now.getMinutes() < 10 ? '0' : '') + now.getMinutes()

  messages.value.push({
    sender: 'user',
    text: newMessage.value,
    time: timeString,
  })

  newMessage.value = ''

  // Scroll to bottom
  nextTick(() => {
    scrollToBottom()
  })

  // Simulate response after a short delay
  setTimeout(() => {
    // Add support response
    messages.value.push({
      sender: 'support',
      text: 'Thank you for your message! Our team will get back to you shortly.',
      time: timeString,
    })

    // Scroll to bottom again
    nextTick(() => {
      scrollToBottom()
    })
  }, 1000)
}

const scrollToBottom = () => {
  if (messagesContainer.value) {
    messagesContainer.value.scrollTop = messagesContainer.value.scrollHeight
  }
}

const dismissNotification = () => {
  showNotification.value = false
}
const parent_id = ref('')
// Simulate new message after a delay
onBeforeMount(async () => {

parent_id.value = authStore.parent_pid.replace(/"/g, '')
await  studentStore.fetchByParentID(parent_id.value)
})
onMounted( async () => {

  await dbCountStore.getEventCount(authStore.user_pid, authStore.school_id)
  setTimeout(() => {
    const now = new Date()
    const timeString = now.getHours() + ':' + (now.getMinutes() < 10 ? '0' : '') + now.getMinutes()

    messages.value.push({
      sender: 'support',
      text: 'Hi there! Just checking if you need any assistance with your account?',
      time: timeString,
    })

    if (!isChatOpen.value) {
      unreadCount.value = 2
      showNotification.value = true

      // Auto-hide notification after 5 seconds
      setTimeout(() => {
        if (!isChatOpen.value) {
          showNotification.value = false
        }
      }, 5000)
    } else {
      nextTick(() => {
        scrollToBottom()
      })
    }
  }, 5000)
})
</script>

<style scoped>
/* Base styles */
.dashboard-container {
  width: 100%;
  max-width: 1280px;
  margin: 0 auto;
  padding: 0.75rem;
  padding-bottom: 5rem; /* Add space at the bottom to make room for chat bubble */
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
}

/* Dashboard Layout with Sidebar - Now conditionally styled */
.dashboard-layout {
  display: grid;
  grid-template-columns: 320px 1fr;
  gap: 1rem;
  flex: 1;
  overflow-y: auto; /* Add scrolling to the layout */
}

/* When no sidebar, adapt layout */
.dashboard-layout.no-sidebar {
  grid-template-columns: 1fr;
  overflow-y: auto;
}

/* Card Groups Styling */
.card-groups {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  width: 100%;
  overflow-y: visible; /* Allow content to overflow */
}

.card-group {
  background-color: rgba(255, 255, 255, 0.5);
  border-radius: 0.75rem;
  padding: 0.75rem;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.3);
  margin-bottom: 0.75rem; /* Add some bottom margin to ensure spacing */
}

.group-title {
  font-size: 1rem;
  font-weight: 600;
  color: #374151;
  margin-bottom: 0.5rem;
  border-bottom: 1px solid rgba(99, 102, 241, 0.3);
  padding-bottom: 0.25rem;
}

/* Events Sidebar Styles */
.events-sidebar {
  height: fit-content;
  max-height: calc(100vh - 6rem);
  overflow-y: auto;
  position: sticky;
  width: 350px;
  min-width: 320px;
}

.events-card {
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.05);
  height: 100%;
}

.upcoming-events {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.event-item {
  display: flex;
  gap: 1rem;
  margin-top: 1rem;
  padding-bottom: 1.5rem;
  border-bottom: 1px solid #f3f4f6;
}

.event-item:last-child {
  border-bottom: none;
  padding-bottom: 0;
}

.event-date {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-width: 60px;
  height: 70px;
  background: linear-gradient(135deg, #f0f9ff, #e0f2fe);
  border-radius: 0.75rem;
  border-left: 3px solid #3b82f6;
  padding: 0.5rem;
}

.event-month {
  font-size: 0.875rem;
  font-weight: 500;
  color: #3b82f6;
  text-transform: uppercase;
}

.event-day {
  font-size: 1.5rem;
  font-weight: 700;
  color: #1e40af;
}

.event-details {
  flex: 1;
}

.event-title {
  font-weight: 600;
  font-size: 1rem;
  color: #1f2937;
  margin-bottom: 0.5rem;
}

.event-time,
.event-location {
  font-size: 0.875rem;
  color: #6b7280;
  display: flex;
  align-items: center;
  margin-bottom: 0.25rem;
}

/* Card Grid Layout - Fixed 4 cards per row */
.card-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 0.75rem;
  margin-bottom: 0;
}

/* Card Styles */
.dashboard-card {
  position: relative;
  overflow: hidden;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  height: 100%;
  transition: all 0.3s ease;
  display: flex;
  flex-direction: column;
  padding: 0.65rem !important;
  min-height: 140px;
  max-height: none; /* Remove max height to prevent content from being cut off */
}

.dashboard-card:hover {
  box-shadow:
    0 10px 15px -3px rgba(0, 0, 0, 0.1),
    0 4px 6px -2px rgba(0, 0, 0, 0.05);
}

.dashboard-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 0;
  background: linear-gradient(to right, rgba(99, 102, 241, 0.1), rgba(168, 85, 247, 0.1));
  transition: height 0.3s ease-in-out;
  z-index: -1;
}

.dashboard-card:hover::before {
  height: 100%;
}

/* Chat Bubble Styles - Fixed position, unaffected by layout changes */
.chat-bubble-container {
  position: fixed;
  right: 2rem;
  bottom: 2rem;
  z-index: 1000;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
}

.chat-bubble-button {
  width: 4rem;
  height: 4rem;
  border-radius: 50%;
  background: linear-gradient(135deg, #6366f1, #8b5cf6);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 15px rgba(99, 102, 241, 0.4);
  border: none;
  cursor: pointer;
  font-size: 1.5rem;
  transition: all 0.3s ease;
  position: relative;
  z-index: 1010;
}

.chat-bubble-button:hover {
  transform: scale(1.05);
  box-shadow: 0 6px 20px rgba(99, 102, 241, 0.5);
}

.chat-bubble-button.active {
  background: linear-gradient(135deg, #ef4444, #f43f5e);
  box-shadow: 0 4px 15px rgba(239, 68, 68, 0.4);
}

.chat-bubble-badge {
  position: absolute !important;
  top: -5px !important;
  right: -5px !important;
  font-size: 0.75rem !important;
  font-weight: bold !important;
}

/* Chat Box Styles */
.chat-box {
  position: fixed;
  bottom: 7rem;
  right: 2rem;
  width: 350px;
  height: 450px;
  background-color: white;
  border-radius: 1rem;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15);
  z-index: 1000;
}

.chat-header {
  background: linear-gradient(135deg, #f9fafb, #f3f4f6);
  padding: 1rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid #e5e7eb;
}

.chat-header-button {
  width: 2rem;
  height: 2rem;
  border-radius: 50%;
  background-color: #f3f4f6;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6b7280;
  cursor: pointer;
  transition: all 0.2s;
}

.chat-header-button:hover {
  background-color: #e5e7eb;
  color: #4b5563;
}
/* Card Container Styling */
.card-container {
  width: 100%;
  background-color: rgba(255, 255, 255, 0.8);
  margin-bottom: 1.5rem;
}

/* Group Header Styling */
.group-header {
  display: flex;
  align-items: center;
  margin-bottom: 1.5rem;
  width: 100%;
}

.group-title {
  font-size: 1.1rem;
  font-weight: 600;
  color: #374151;
  padding: 0 1rem;
  white-space: nowrap;
}

.line-left,
.line-right {
  height: 1px;
  background-color: rgba(99, 102, 241, 0.3);
  flex-grow: 1;
}

/* Card Grid Layout */
.card-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 0.75rem;
  margin-bottom: 0;
}

/* Remove double backgrounds and nested shadows from cards */
.dashboard-card {
  background: none !important;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05) !important;
  min-height: 140px;
  height: 100%;
}

/* Responsive adjustments */
@media (max-width: 1280px) {
  .card-grid {
    grid-template-columns: repeat(3, 1fr);
  }
}

@media (max-width: 1024px) {
  .card-grid {
    grid-template-columns: repeat(3, 1fr);
  }
}

@media (max-width: 768px) {
  .card-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 640px) {
  .card-grid {
    grid-template-columns: 1fr;
  }
}
.chat-messages {
  flex: 1;
  padding: 1rem;
  overflow-y: auto;
  background-color: #f9fafb;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.message {
  max-width: 75%;
  padding: 0.5rem 0.75rem;
  border-radius: 1rem;
  position: relative;
  animation: message-pop 0.3s ease;
}

.message-support {
  align-self: flex-start;
  background-color: #f3f4f6;
  color: #374151;
  border-bottom-left-radius: 0.25rem;
}

.message-user {
  align-self: flex-end;
  background: linear-gradient(135deg, #6366f1, #8b5cf6);
  color: white;
  border-bottom-right-radius: 0.25rem;
}

.message-text {
  font-size: 0.9rem;
  line-height: 1.4;
}

.message-time {
  font-size: 0.65rem;
  opacity: 0.7;
  margin-top: 0.25rem;
  text-align: right;
}

.chat-input {
  padding: 0.75rem;
  background-color: white;
  border-top: 1px solid #e5e7eb;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.chat-input-field {
  flex: 1;
  padding: 0.6rem 1rem;
  border-radius: 1.5rem;
  border: 1px solid #e5e7eb;
  font-size: 0.9rem;
  outline: none;
  transition: all 0.2s;
}

.chat-input-field:focus {
  border-color: #6366f1;
  box-shadow: 0 0 0 2px rgba(99, 102, 241, 0.2);
}

.chat-input-button {
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 50%;
  background-color: #f3f4f6;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6b7280;
  cursor: pointer;
  transition: all 0.2s;
}

.chat-input-button:hover {
  background-color: #e5e7eb;
  color: #4b5563;
}

.chat-input-button:last-child {
  background: linear-gradient(135deg, #6366f1, #8b5cf6);
  color: white;
}

.chat-input-button:last-child:hover {
  background: linear-gradient(135deg, #4f46e5, #7c3aed);
}

/* Notification Styles */
.chat-notification {
  position: fixed;
  bottom: 7rem;
  right: 2rem;
  width: 300px;
  background-color: white;
  padding: 1rem;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
  cursor: pointer;
  animation: notification-slide 0.3s ease;
  display: flex;
  justify-content: space-between;
  z-index: 1000;
}

.notification-close {
  width: 1.5rem;
  height: 1.5rem;
  border-radius: 50%;
  background-color: #f3f4f6;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6b7280;
  cursor: pointer;
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
  transition: all 0.2s;
}

.notification-close:hover {
  background-color: #e5e7eb;
  color: #4b5563;
}

/* Animations */
@keyframes message-pop {
  0% {
    opacity: 0;
    transform: scale(0.9);
  }
  100% {
    opacity: 1;
    transform: scale(1);
  }
}

@keyframes notification-slide {
  0% {
    opacity: 0;
    transform: translateY(20px);
  }
  100% {
    opacity: 1;
    transform: translateY(0);
  }
}

.chat-slide-enter-active,
.chat-slide-leave-active {
  transition: all 0.3s ease;
}

.chat-slide-enter-from,
.chat-slide-leave-to {
  opacity: 0;
  transform: translateY(20px);
}

.notification-pop-enter-active,
.notification-pop-leave-active {
  transition: all 0.3s ease;
}

.notification-pop-enter-from,
.notification-pop-leave-to {
  opacity: 0;
  transform: translateY(20px);
}

/* Add a subtle pulse animation to the badge notifications */
:deep(.p-badge) {
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0% {
    box-shadow: 0 0 0 0 rgba(239, 68, 68, 0.4);
  }
  70% {
    box-shadow: 0 0 0 6px rgba(239, 68, 68, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(239, 68, 68, 0);
  }
}

/* Improve button hover states */
:deep(.p-button) {
  transition: all 0.2s ease;
}

:deep(.p-button:hover) {
  transform: translateY(-2px);
}

/* Responsive layouts */
@media (max-width: 1280px) {
  .card-grid {
    grid-template-columns: repeat(3, 1fr);
  }
}

@media (max-width: 1024px) {
  .dashboard-layout {
    grid-template-columns: 1fr;
  }

  .dashboard-layout.no-sidebar {
    grid-template-columns: 1fr;
  }

  .events-sidebar {
    margin-bottom: 0.75rem;
    width: 100%;
    min-width: 100%;
  }

  .card-grid {
    grid-template-columns: repeat(4, 1fr);
  }
}

@media (max-width: 768px) {
  .dashboard-container {
    padding: 0.5rem;
  }

  .card-grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .chat-box {
    width: calc(100vw - 4rem);
    right: 2rem;
  }

  .chat-notification {
    width: calc(100vw - 4rem);
    right: 2rem;
  }
}

@media (max-width: 640px) {
  .dashboard-container {
    padding: 1rem;
  }

  .card-grid {
    grid-template-columns: 1fr;
  }

  .event-item {
    flex-direction: column;
    gap: 0.75rem;
  }

  .event-date {
    flex-direction: row;
    height: auto;
    width: 100%;
    justify-content: flex-start;
    gap: 0.5rem;
    padding: 0.5rem;
  }

  .chat-bubble-container {
    right: 1rem;
    bottom: 1rem;
  }

  .chat-bubble-button {
    width: 3.5rem;
    height: 3.5rem;
  }

  .chat-box {
    right: 1rem;
    bottom: 5.5rem;
    width: calc(100vw - 2rem);
  }

  .chat-notification {
    right: 1rem;
    bottom: 5.5rem;
    width: calc(100vw - 2rem);
  }
}
</style>
